import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';

const FAQS = [
  {
    question: 'What does an AI file organizer for accountants actually do?',
    answer:
      "It reads each text-bearing PDF on your Mac (downloaded e-invoices, brokerage statements, 1099 PDFs, searchable scans), extracts the vendor, date, amount, document type, and which client or entity it belongs to, then routes it into the correct client folder by tax year and renames it consistently. You choose the AI that runs the classification: our cloud by default, your own API key (BYOK), or a fully local LLM like Ollama. Most CPAs install it during tax season and never go back.",
  },
  {
    question: 'How does Sortio automate accounting file organization for tax season?',
    answer:
      "The standard pattern: watch the Downloads folder and a shared per-client intake folder. New PDFs are read, named from content (vendor, date, amount), and routed under Client / Tax Year / Document Type. A 1099-NEC for client Acme from payer XYZ lands at Acme/2026/1099s/XYZ_1099-NEC_2026.pdf without you touching it. An emailed Uber receipt PDF lands in Acme/2026/Receipts/2026-03-14_Uber_$24.80.pdf.",
  },
  {
    question: 'How does Sortio auto rename receipts for CPAs?',
    answer:
      "Digital receipts are recognized as a document type and renamed using the pattern date_vendor_amount.pdf. An emailed Office Depot receipt PDF turns into 2026-03-14_OfficeDepot_$54.12.pdf. One scope note: Sortio reads the text inside a file, so photographed receipts (JPGs with no text) need an OCR pass first; a free tool like OCRmyPDF turns a scan into a searchable PDF Sortio can read. For a deep-dive on the technique (and the prompt template behind it), see our automatically rename PDFs by content guide, which uses the same engine.",
  },
  {
    question: 'Can Sortio route 1099-NEC, 1099-MISC, K-1, and W-2 forms separately?',
    answer:
      "Yes. Sortio recognizes each tax form by its text content (form number, issuing entity, recipient name, tax year) and routes them into separate subfolders. A client zip with twelve 1099s, three K-1s, and two W-2s gets unpacked into Client/2026/1099s/, Client/2026/K-1s/, Client/2026/W-2s/ in one pass. The Rule Builder can refine the structure further (for example splitting 1099-DIV from 1099-INT inside the 1099s folder).",
  },
  {
    question: 'Will receipts and statements with SSNs or account numbers leave my Mac?',
    answer:
      "Not unless you pick the cloud classifier. In Ollama mode no file content (and no filenames) crosses the network. In BYOK mode requests go from your Mac directly to your provider on your account, never through Sortio. With the default Sortio classifier, filenames and extracted entities cross the wire but are redacted from server-side logs after 30 days. For CPAs handling SSNs and account numbers, we recommend Ollama or BYOK.",
  },
  {
    question: 'Does Sortio integrate with QuickBooks, Xero, or my tax software?',
    answer:
      "Not directly today. What it does instead: work on any folder your tax software can read or export to. Most modern stacks (UltraTax, Drake, ProSeries, Lacerte, QuickBooks Online exports) write to or read from a local folder, and Sortio organizes that folder. The Buildium and Rent Manager integrations are shipping in the same wave for the property managers who use them, but generic accounting integration is filesystem-only for now.",
  },
  {
    question: 'How do I structure my client folders so Sortio routes correctly?',
    answer:
      "Sortio adapts to whatever you already have. Most accountants land on Clients/<Last Name First Name>/<Tax Year>/<Document Type>, or for entity work Clients/<Entity Name>/<Year>/<Document Type>. Tell Sortio your structure once via the Rule Builder, turn on \"Restrict to a folder list\", and it routes new arrivals into matching folders only (with a fallback bucket for unknown clients).",
  },
  {
    question: 'What about year-end archive prep when I close out a tax year?',
    answer:
      "The Rule Builder packs each closed year into a read-only archive named with the filing date. Working drafts move to a superseded subfolder, the receipts and statements get split by month for clean reconciliation, and the audit log captures every move so you can reproduce the year three or five years later if an IRS notice arrives.",
  },
  {
    question: 'How fast is it on a 2,000-receipt March pile?',
    answer:
      "AI Sort runs up to 5,000 files per pass on Pro and a 2,000-file receipt-PDF pile typically completes in a few minutes on a modern Mac. For multi-year archives (10,000 plus files), use the Rule Builder. Rule sorts process unlimited files instantly and never touch your AI allowance. The pattern we recommend for new users: AI Sort the most recent month's pile to teach Sortio your conventions, then build a rule from that and run the rule over the year's backlog.",
  },
  {
    question: 'Can a bookkeeping client run Sortio on their end before sending me files?',
    answer:
      "Yes, and many do. The Free tier lets a client collect their emailed receipts and statements in a single folder all month, then run a one-click Sortio sort before sending you the clean per-month receipt folder. Cuts your March intake time and lets you start work with named, dated files instead of a heap of receipt(7).pdf.",
  },
  {
    question: 'Does Sortio handle multi-entity clients (S-corp plus personal 1040)?',
    answer:
      "Yes. Each entity is a separate root folder. The Rule Builder lets you say \"if a file mentions Acme Holdings LLC, file under the entity; if it mentions John Smith personally and is not vehicle-related to the LLC, file under the 1040 folder.\" Mixed entity returns stay separate, and you can audit the routing decision per file.",
  },
  {
    question: 'What does Sortio cost for a solo CPA?',
    answer:
      "The free tier gives you a one-time AI trial allowance, enough to try a real client folder before paying. Pro is $14.99 per month or $99 per year, including a monthly AI allowance sized for heavy use, AI sort up to 5,000 files per run, auto-sort on file change, and BYOK so you can plug in your own provider. Team is $29 per seat per month for shops where multiple preparers share a rule library.",
  },
  {
    question: 'Does it work on Windows too, or just Mac?',
    answer:
      "Sortio ships for macOS, Windows, and Linux. Many CPAs run it on a Mac connected to a scanner; bookkeeping shops often run it on Windows next to QuickBooks Desktop. Same product, same Pro pricing.",
  },
  {
    question: 'How do I get started with one client folder?',
    answer:
      "Download Free, point Sortio at a single client's current tax-year working folder, and run AI Sort. Approve the preview. Once you like the routing, open the Rule Builder and turn the routing into a saved rule. Apply that rule to your other clients. Most accountants reach steady state on their full book within a weekend.",
  },
];

const FAQItem = ({ question, answer, isOpen, onToggle }) => (
  <div className="mb-4 transition-all duration-300 transform hover:translate-x-1">
    <div className={`apple-panel transition-all duration-300 ${isOpen ? 'ring-1 ring-sortio-primary/30' : ''}`}>
      <button className="flex justify-between items-center w-full p-5 text-left rounded-apple focus:outline-none" onClick={onToggle} aria-expanded={isOpen}>
        <span className="text-base font-medium text-apple-50 pr-4">{question}</span>
        <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors duration-300 ${isOpen ? 'bg-sortio-primary/20' : 'bg-apple-600/40'}`}>
          {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-sortio-primary" /> : <ChevronDown className="w-3.5 h-3.5 text-apple-300" />}
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="p-5 pt-0">
          <p className="text-apple-300 text-sm leading-relaxed">{answer}</p>
        </div>
      </div>
    </div>
  </div>
);

const AccountantsFAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  };

  return (
    <section id="accountants-faq" className="my-20">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="flex flex-col items-center mb-12">
        <div className="mb-4 rounded-full bg-apple-700/60 backdrop-blur-sm p-2 border border-apple-600/20">
          <HelpCircle className="w-6 h-6 text-sortio-primary" />
        </div>
        <h2 className="text-3xl sm:text-4xl font-semibold mb-4 tracking-tight text-apple-50 text-center">
          Accountant <span className="apple-gradient-text">FAQ</span>
        </h2>
        <p className="text-apple-300 text-lg font-light max-w-2xl text-center">
          Questions from solo CPAs, EAs, bookkeeping shops, and tax preparers evaluating Sortio for
          client folder discipline and tax-season throughput.
        </p>
      </div>

      <div className="max-w-3xl mx-auto">
        {FAQS.map((faq, index) => (
          <FAQItem key={index} question={faq.question} answer={faq.answer} isOpen={openIndex === index} onToggle={() => setOpenIndex(openIndex === index ? -1 : index)} />
        ))}
      </div>
    </section>
  );
};

export default AccountantsFAQ;
